import type { ReactElement, ReactNode } from "react";
import { memo, useCallback, useState } from "react";

import { Combobox } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import type { IntegrationAccount } from "app/pages/sales-intelligence/modules/company-analysis/types/crm-links";

import { StyledAccountName, StyledAccountRow } from "./styles";

export type AccountsDropdownProps = {
    accounts: readonly IntegrationAccount[];
    ariaLabel: string;
    dataAutomationPrefix: string;
    children: ReactNode;
    onAccountClick?: (account: IntegrationAccount) => void;
};

const getAccountOwnerText = ({ ownerName }: IntegrationAccount): string =>
    translate("si.account-overview.integration-tag.multiple.owned-by", { ownerName });

const AccountsDropdown = (props: AccountsDropdownProps) => {
    const { accounts, ariaLabel, dataAutomationPrefix, children, onAccountClick } = props;
    const [isOpen, setIsOpen] = useState(false);

    const handleAccountSelect = useCallback(
        (account: IntegrationAccount): void => {
            onAccountClick?.(account);
            setIsOpen(false);
            window.open(account.link, "_blank", "noopener,noreferrer");
        },
        [onAccountClick],
    );

    const renderAccountOption = (account: IntegrationAccount, index: number): ReactElement => {
        const ownerText = getAccountOwnerText(account);

        return (
            <Combobox.Option
                key={account.crmAccountId}
                id={account.crmAccountId}
                textValue={`${account.companyName} ${ownerText}`}
                data-automation={`${dataAutomationPrefix}-item-${index}`}
                onClick={() => handleAccountSelect(account)}
            >
                <StyledAccountRow>
                    <StyledAccountName>{account.companyName}</StyledAccountName>
                    {account?.ownerName ? <span>{ownerText}</span> : null}
                </StyledAccountRow>
            </Combobox.Option>
        );
    };

    return (
        <Combobox
            placement="bottom-start"
            isOpen={isOpen}
            onOpenChange={setIsOpen}
        >
            <Combobox.Trigger>{children}</Combobox.Trigger>
            <Combobox.Content
                ariaLabel={ariaLabel}
                data-automation={`${dataAutomationPrefix}-menu`}
            >
                {accounts.map(renderAccountOption)}
            </Combobox.Content>
        </Combobox>
    );
};

export default memo(AccountsDropdown);
